import { useState } from "react";
import { apiClient } from "../services";
import { toast } from "../hooks/useToast";
import { useCartStore } from "../stores/cartStore";

/**
 * Interfaz para la respuesta de iniciación de pago
 */
export interface PaymentInitiationResponse {
  success: boolean;
  data: {
    token: string;
    url: string;
    checkoutSessionId: string;
  };
  message: string;
}

/**
 * Interfaz para el error de pago
 */
export interface PaymentError {
  error: string;
  message: string;
  details?: string;
}

/**
 * Hook para iniciar un pago con Transbank
 *
 * @returns Hook personalizado para iniciar pagos
 */
export function useInitiatePayment() {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (): Promise<PaymentInitiationResponse> => {
    setIsPending(true);

    try {
      // Verificar estado del carrito antes de iniciar el pago
      const { getTotalPrice, items, isAnonymous, anonymousSessionToken } =
        useCartStore.getState();
      const cartTotal = getTotalPrice();

      console.log("🔄 Estado del carrito antes del pago:", {
        totalPrice: cartTotal,
        itemCount: items.length,
        isAnonymous,
        hasAnonymousToken: !!anonymousSessionToken,
      });

      // Determinar qué endpoint usar basado en si es usuario anónimo
      const endpoint = isAnonymous
        ? "/api/v1/payments/initiate-anonymous"
        : "/api/v1/payments/initiate";
      const headers =
        isAnonymous && anonymousSessionToken
          ? { "x-session-token": anonymousSessionToken }
          : {};

      const response = await apiClient.post<PaymentInitiationResponse>(
        endpoint,
        {},
        { headers }
      );
      const data = response.data;

      console.log("✅ Pago iniciado exitosamente:", data);

      // Mostrar mensaje de redirección
      toast({
        title: "Redirigiendo a Transbank",
        description: "Serás redirigido a la pasarela de pago...",
        variant: "default",
      });

      // ✅ FLUJO SIMPLE: Redirección directa a Transbank (sin popup)
      if (data.data?.url && data.data?.token) {
        console.log("🔄 Redirigiendo a Transbank:", data.data.url);

        // Crear formulario temporal para POST a Transbank
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.data.url;
        form.style.display = "none";

        // Agregar el token como campo oculto
        const tokenInput = document.createElement("input");
        tokenInput.type = "hidden";
        tokenInput.name = "token_ws";
        tokenInput.value = data.data.token;
        form.appendChild(tokenInput);

        // Agregar el formulario al DOM y enviarlo
        document.body.appendChild(form);

        console.log("🚀 Redirigiendo a Transbank...");
        form.submit();

        // El usuario será redirigido a Transbank
        // Transbank procesará el pago
        // Transbank redirigirá al return_url del backend
        // El backend procesará el pago y redirigirá al frontend
      } else {
        console.error("❌ Datos de pago incompletos:", data);
        toast({
          title: "Error iniciando pago",
          description:
            "No se pudieron obtener los datos de pago. Intenta nuevamente.",
          variant: "destructive",
        });
      }

      return data;
    } catch (error: any) {
      console.error("❌ Error al iniciar pago:", error);

      let errorMessage = "Error al iniciar el pago";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Error al iniciar pago",
        description: errorMessage,
        variant: "destructive",
      });

      throw error;
    } finally {
      setIsPending(false);
    }
  };


  return {
    mutateAsync,
    isPending,
  };
}

/**
 * Hook para obtener el estado de una transacción de pago
 *
 * @param token Token de la transacción
 * @returns Hook personalizado para obtener el estado
 */
export function usePaymentStatus(token: string | null) {
  const [isPending, setIsPending] = useState(false);

  const mutateAsync = async (): Promise<any> => {
    if (!token) {
      throw new Error("Token de transacción requerido");
    }

    setIsPending(true);

    try {
      const response = await apiClient.get(`/api/v1/payments/status/${token}`);
      return response.data;
    } catch (error: any) {
      console.error("❌ Error al obtener estado de pago:", error);

      toast({
        title: "Error",
        description: "No se pudo obtener el estado del pago",
        variant: "destructive",
      });

      throw error;
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutateAsync,
    isPending,
  };
}
